/**
 * FeedbackPage.tsx — 用户反馈页（Inbox 布局：左侧工单列表 + 右侧详情/回复）ADM-FBK-S001~S004
 *
 * 1:1 对照原型 design/Novel-admin/src/pages/feedback.jsx（2026-06 新版）+ 全量接真：
 * - 列表走 useFeedback()（GET /api/v1/feedback）；详情走 useFeedbackDetail(id)
 *   （GET /api/v1/feedback/:id + N+1 用户富集），正文 / 中文翻译 / 回复 thread 均真数据。
 * - 发送回复 → POST /:id/reply（content + is_internal_note + auto_translated，ADM-FBK-S002/S003；
 *   翻译由服务端 DeepL 提交时执行，失败返 FEEDBACK_TRANSLATION_FAILED 不落库 → 错误 toast + 草稿保留）。
 * - 关闭工单 → POST /:id/close（ConfirmDialog 二次确认；服务端写 feedback_status_log）。
 * - 草稿：localStorage 真存真恢复（key na-feedback-reply-draft:<id>，发送成功后清除）。
 * - 数据无源的设计元素不编数据（详见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）：
 *   未读红点（DTO 无 has_unread_reply）→ 不渲染；App 筛选走顶栏 app-scope，
 *   详情头 App chip 按 feedback.app_id 经 apps 表解析。
 * - 「BONUS 发放」入口按原型打开 users 域 GrantBonusDialog（grant-bonus 注册弹窗，本页不自造）。
 * - 前端 RBAC（adversarial F1/F2）：reply=root/admin/operator、close=root/admin、BONUS=root
 *   （与 server feedback / finance route 的 requireRole 精确一致）；非授权角色对应控件 disabled+title，
 *   不发注定 403 的写请求（前端只做 UX 门控，后端 requireRole 才是权威）。
 */

import {
  FEEDBACK_ATTACHMENT_CONTENT_TYPES,
  FEEDBACK_ATTACHMENT_MAX_SIZE_BYTES,
  type ReplyFeedbackRequest,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import type { FeedbackRow } from '~/data/queries';
import { useFeedback, useFeedbackDetail, useSysConfig } from '~/data/queries';
import { feedbackApi, putAttachmentToSignedUrl } from '~/hooks/use-feedback';
import { fmtDate, fmtDateTime, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { FeedbackAttachmentThumb } from '~/pages/feedback/FeedbackAttachmentThumb';
import { GrantBonusDialog } from '~/pages/users/dialogs/GrantBonusDialog';
import { useAuthStore } from '~/stores/auth-store';
import { Avatar } from '~/ui/Avatar';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { SearchInput } from '~/ui/SearchInput';
import { PageSkeleton } from '~/ui/Skeleton';
import { toast } from '~/ui/Toast';
import { FeedbackUserDetailDrawer, hasFeedbackAccount } from './FeedbackUserDetailDrawer';

/** 反馈分类闭集（dev-docs 90-反馈 feedback.category）+ 中文标签 */
const CATS = ['PAYMENT', 'CONTENT', 'BUG', 'SUGGESTION', 'ACCOUNT', 'OTHER'];
const CAT_ZH: Record<string, string> = {
  PAYMENT: '支付',
  CONTENT: '内容',
  BUG: '缺陷',
  SUGGESTION: '建议',
  ACCOUNT: '账号',
  OTHER: '其他',
};

/** NOT-in-scope 记账文案（disabled 按钮 title 统一指向计划 §五） */
const PLAN_REF = 'docs/plans/2026-06-12-web-real-api-integration-plan.md §五';

// 角色不足时写动作入口的统一禁用标注（前端 RBAC 防线；后端 requireRole 才是权威）
const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

/**
 * 反馈「回复 / 内部备注」写权限（root/admin/operator）—— 后端 POST /feedback/:id/reply
 * requireRole('root','admin','operator')。viewer 看到注定 403 的发送按钮是 bug → 禁用入口。
 * 前端只做 UX 门控，后端 requireRole 为权威（参照 UsersPage 的 useCanModerateUsers）。
 */
function useCanReplyFeedback(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

/**
 * 反馈「关闭工单」写权限（root/admin）—— 后端 POST /feedback/:id/close
 * requireRole('root','admin')。operator/viewer 禁用入口。
 */
function useCanCloseFeedback(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}

/**
 * 发放 BONUS 写权限（root 专属）—— 后端 POST /finance/grant-bonus requireRole('root')（money 路径）。
 * 与 UsersPage 的 useCanGrantBonus 同一闭集；非 root 禁用 feedback 侧 BONUS 入口（F2）。
 */
function useCanGrantBonus(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root';
}

export default function FeedbackPage() {
  // F7：useFeedback 跨页累积全部工单（rows）+ truncated（是否命中累积上限、server 仍有更多页）。
  // 客户端 tab 筛选 + 待回复 count 都在 rows 全集上算 → 靠后 OPEN 工单不再静默消失；若 truncated 则
  // 在统计处诚实标注「仅已加载范围」，不假装全量（adversarial F7）。
  const { data: feedbackView, isLoading: feedbackLoading } = useFeedback();
  const feedback = feedbackView.rows;
  const truncated = feedbackView.truncated;
  const { data: sysConfig } = useSysConfig();
  const queryClient = useQueryClient();
  // 写动作前端 RBAC 门控（F1/F2）：reply=root/admin/operator、close=root/admin、BONUS=root
  // （与 server feedback / finance route requireRole 精确一致；后端 requireRole 才是权威）
  const canReply = useCanReplyFeedback();
  const canClose = useCanCloseFeedback();
  const canGrantBonus = useCanGrantBonus();

  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState<string | null>(null);
  // 语言 / 分类维度筛选 + 关键字（本页内部受控；对照设计稿 feedback.jsx）。
  // App 维度不在本页维护：ADM-FBK-S001 接真后走顶栏 app-scope（useFeedback 读 useAppScope().appId 服务端筛选，
  // P1-web-design-system §4），列表已是当前 App 作用域结果，页内不再设 appFilter 态。
  const [langFilter, setLangFilter] = useState<string[]>([]);
  const [catFilter, setCatFilter] = useState<string[]>([]);
  const [q, setQ] = useState('');

  const list = feedback.filter((f) => {
    if (filter === 'todo' && f.status !== '待回复') return false;
    if (filter === 'doing' && f.status !== '处理中') return false;
    if (filter === 'done' && !(f.status === '已回复' || (f.status === '已关闭' && !f.autoClosed)))
      return false;
    if (filter === 'auto-closed' && !(f.status === '已关闭' && f.autoClosed)) return false;
    if (langFilter.length && !langFilter.includes(f.lang)) return false;
    if (catFilter.length && !(f.category && catFilter.includes(f.category))) return false;
    if (
      q &&
      !(
        f.subject.toLowerCase().includes(q.toLowerCase()) ||
        (f.contactEmail ?? '').toLowerCase().includes(q.toLowerCase()) ||
        f.id.toLowerCase().includes(q.toLowerCase())
      )
    )
      return false;
    return true;
  });

  // current 从当前筛选的可见 list 解析（非全量 feedback）：selected 不在当前 tab/筛选则回退 list[0]，
  // detail 永远指向可见工单，避免切 tab 后 reply/close 操作到隐藏的 stale 工单（codex #14）
  const current = list.find((f) => f.id === selected) ?? list[0];

  // 列表首屏 loading → 路由级骨架（feedback = 表格型，FilterSkeleton + TableSkeleton；对照原型
  // App.jsx 加载态渲染 <PageSkeleton page>）。本页是 Inbox master-detail，右侧详情自己的加载态
  // 在 FeedbackDetail 内就地处理（不套整页 DetailSkeleton，避免破坏左列表 + 右详情同卡布局）。
  if (feedbackLoading) return <PageSkeleton page="feedback" />;

  return (
    <div
      className="page page-enter"
      style={{ paddingBottom: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <div className="page-header">
        <div>
          <h1 className="page-title">用户反馈</h1>
          <div className="page-sub">
            {feedback.filter((f) => f.status === '待回复').length} 待回复 ·{' '}
            {feedback.filter((f) => f.status === '处理中').length} 处理中 · SLA &lt;8h
            {/* F7：累积命中上限（server 仍有更多页）→ 诚实标注统计仅覆盖已加载范围，不假装全量 */}
            {truncated && (
              <span
                style={{ marginLeft: 6, color: 'var(--warn)' }}
                title={`已加载工单超过累积上限，server 仍有更多页未拉取 · 当前统计与列表仅覆盖已加载范围 · ${PLAN_REF}`}
              >
                · 仅已加载范围
              </span>
            )}
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          <SearchInput
            width={210}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="主题 / 邮箱 / 工单 ID"
          />
          {/* ADM-FBK-S001：App 维度筛选已接真——走顶栏 AppScopeSelect + app-scope-store（P1-web-design-system §4，
              不各页自造 App dropdown）；useFeedback 读 useAppScope().appId 服务端按 app_id 筛选。故此处不再有页内 App 按钮。 */}
          <FilterButton
            label="语言"
            value={langFilter}
            onChange={setLangFilter}
            options={sysConfig.languages
              .filter((l) => l.enabled)
              .map((l) => ({
                value: l.language_code,
                label: `${l.language_code.toUpperCase()} ${l.display_name_i18n.zh}`,
              }))}
            width={170}
          />
          <FilterButton
            label="分类"
            value={catFilter}
            onChange={setCatFilter}
            options={CATS.map((c) => ({ value: c, label: `${CAT_ZH[c]} (${c})` }))}
            width={170}
          />
          <button
            className="btn"
            title="刷新列表与详情"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['feedback'] });
              queryClient.invalidateQueries({ queryKey: ['feedback-detail'] });
            }}
          >
            <Icons.IconRefresh size={13} />
          </button>
        </div>
      </div>

      <div className="card" style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Inbox list */}
        <div
          style={{
            width: 360,
            borderRight: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div
            className="hstack"
            style={{
              padding: 10,
              borderBottom: '1px solid var(--border)',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <div className="seg" style={{ flex: 1 }}>
              <button
                className={filter === 'all' ? 'on' : ''}
                onClick={() => setFilter('all')}
                style={{ flex: 1 }}
              >
                全部 {feedback.length}
              </button>
              <button
                className={filter === 'todo' ? 'on' : ''}
                onClick={() => setFilter('todo')}
                style={{ flex: 1 }}
              >
                待回复 {feedback.filter((f) => f.status === '待回复').length}
              </button>
              <button
                className={filter === 'doing' ? 'on' : ''}
                onClick={() => setFilter('doing')}
                style={{ flex: 1 }}
              >
                处理中 {feedback.filter((f) => f.status === '处理中').length}
              </button>
              <button
                className={filter === 'done' ? 'on' : ''}
                onClick={() => setFilter('done')}
                style={{ flex: 1 }}
              >
                已处理{' '}
                {
                  feedback.filter(
                    (f) => f.status === '已回复' || (f.status === '已关闭' && !f.autoClosed),
                  ).length
                }
              </button>
              {/* ADM-FBK-S004 自动关闭筛选：接真 auto_closed_by==='SYSTEM'（cron 自动关单）→ autoClosed=true */}
              <button
                className={filter === 'auto-closed' ? 'on' : ''}
                onClick={() => setFilter('auto-closed')}
                style={{ flex: 1 }}
              >
                自动关闭 {feedback.filter((f) => f.status === '已关闭' && f.autoClosed).length}
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {list.map((f) => {
              const active = current?.id === f.id;
              return (
                <div
                  key={f.id}
                  onClick={() => setSelected(f.id)}
                  style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: active ? 'var(--accent-soft)' : 'transparent',
                    position: 'relative',
                  }}
                >
                  {active && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        bottom: 8,
                        width: 2,
                        background: 'var(--accent)',
                        borderRadius: 2,
                      }}
                    />
                  )}
                  {/* 设计稿未读红点（hasUnreadReply）数据无源：admin 列表 DTO 无 has_unread_reply，
                      详情 replies 仅运营回复、无用户续回复可推导 → 不渲染（见计划 §五） */}
                  <div className="hstack" style={{ gap: 6, marginBottom: 4 }}>
                    <Avatar seed={f.id} size={22} fontSize={10} style={{ flexShrink: 0 }}>
                      {f.user.name[0]}
                    </Avatar>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {f.user.name}
                    </span>
                    <span
                      className={`chip ${f.status === '待回复' ? 'chip-warn' : f.status === '已回复' ? 'chip-pos' : f.status === '处理中' ? 'chip-info' : ''}`}
                      style={{ fontSize: 9.5, padding: '0 5px' }}
                    >
                      {f.autoClosed ? '自动关闭' : f.status}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: active ? 'var(--fg-strong)' : 'var(--fg)',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {f.subject}…
                  </div>
                  <div
                    className="hstack"
                    style={{ marginTop: 4, gap: 6, fontSize: 10, color: 'var(--fg-faint)' }}
                  >
                    {f.category && (
                      <span className="chip" style={{ fontSize: 9, padding: '0 4px' }}>
                        {CAT_ZH[f.category] || f.category}
                      </span>
                    )}
                    <span className="mono">{f.appName}</span>
                    <span>·</span>
                    {f.contactEmail && (
                      <>
                        <span
                          className="mono"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 110,
                          }}
                          title={`contact_email · ${f.contactEmail}`}
                        >
                          {f.contactEmail}
                        </span>
                        <span>·</span>
                      </>
                    )}
                    <span className="mono">{fmtDate(f.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        {current && (
          <FeedbackDetail
            key={current.id}
            item={current}
            canReply={canReply}
            canClose={canClose}
            canGrantBonus={canGrantBonus}
          />
        )}
      </div>
    </div>
  );
}

function FeedbackDetail({
  item,
  canReply,
  canClose,
  canGrantBonus,
}: {
  item: FeedbackRow;
  /** reply/内部备注写权限（root/admin/operator）；前端 UX 门控，后端 requireRole 为权威（F1） */
  canReply: boolean;
  /** 关闭工单写权限（root/admin）（F1） */
  canClose: boolean;
  /** 发放 BONUS 写权限（root）（F2） */
  canGrantBonus: boolean;
}) {
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: view, isLoading } = useFeedbackDetail(item.id);
  const detail = view?.detail ?? null;
  const enrichedUser = view?.user ?? null;
  const hasAccount = hasFeedbackAccount(item.user.id);
  const canGrantAccountBonus = canGrantBonus && hasAccount;

  // 展示名 / 邮箱：N+1 用户富集优先（display_name），缺省回退列表行（user_id 占位）
  const displayName = enrichedUser?.display_name ?? item.user.name;
  const contactEmail = detail?.contact_email ?? item.contactEmail ?? '';
  const appName = view?.feedbackAppName ?? '—';

  const [autoTrans, setAutoTrans] = useState(true);
  const [internalNote, setInternalNote] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  // 最近一次发送因 FEEDBACK_TRANSLATION_FAILED 失败（真实失败态，对应设计稿 failed 态机）。
  // 该 422 是**确定性**失败：server 先译后单事务入库，译失败抛在事务前 → **不落库**，故同草稿直接重试安全。
  const [transFailed, setTransFailed] = useState(false);
  // F6：最近一次发送是**模糊失败**（5xx / 网络中断 / 响应解码失败 / 非确定性错误）——POST 可能已落库但响应丢失。
  // server reply **无 Idempotency-Key**（已查契约，见输出），同草稿直接重试可能产生**重复回复**。故此态下
  // 禁用直接重发，要求运营先看 refetch 回来的 thread 确认是否已发出，再显式决定（acknowledge）后才放行重发。
  const [ambiguousFailure, setAmbiguousFailure] = useState(false);
  // 关闭工单二次确认弹窗 / BONUS 发放弹窗（grant-bonus，users 域注册弹窗复用）
  const [closeConfirm, setCloseConfirm] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  // 流转历史时间线弹窗（detail.status_log，feedback_status_log 镜像）
  const [statusLogOpen, setStatusLogOpen] = useState(false);
  const statusLog = detail?.status_log ?? [];

  /**
   * dd#960 回复附件（A7 §3 单条上限 1）。**选中即上传**（同 C 端提交页口径）：pre-create + PUT 先完成，
   * 拿到 `attachment_id` 后才允许发送 —— 这样发送失败重试时复用同一 id，不会每次重试都产出新 orphan。
   * `previewUrl` 是**本地文件**的 objectURL：绑定前经 §2.2 代理读会返 RESOURCE_NOT_FOUND
   * （该端点校 feedback_id 匹配），拿不到服务端副本，故发送前一律用本地对象预览（A7 §3.5）。
   */
  const [attachment, setAttachment] = useState<{
    id: string;
    previewUrl: string;
    name: string;
    size: number;
  } | null>(null);
  const [attachUploading, setAttachUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 卸载 / 换图时 revoke 本地 objectURL，防泄漏
  useEffect(() => {
    const url = attachment?.previewUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment?.previewUrl]);

  // 草稿：localStorage 真存真恢复（per 工单 key；发送成功清除）
  const draftKey = `na-feedback-reply-draft:${item.id}`;
  const [reply, setReply] = useState(() => window.localStorage.getItem(draftKey) ?? '');
  useEffect(() => {
    setReply(window.localStorage.getItem(draftKey) ?? '');
    setTransFailed(false);
    setAmbiguousFailure(false);
    // 换工单必须清空附件：attachment_id 已 scoped 到上一个工单的 app_id，跨工单引用会被
    // 服务端以 ATTACHMENT_APP_MISMATCH / 归属校验拒掉
    setAttachment(null);
    setUserDetailOpen(false);
  }, [draftKey]);

  const MAX = 5000; // 对齐 ReplyFeedbackRequestSchema content max(5000)
  const over = reply.length > MAX;
  const closed = item.status === '已关闭';
  const translation = detail?.translation ?? null;

  // 对账刷新（F4）：reply/close 后端均返回 { ok: true }（无更新 DTO 可 setQueryData 合并）→
  // 只能 await invalidate 让列表 + 当前详情 refetch 服务端真值；await 让 mutation isPending 覆盖到
  // refetch 落地，关掉「响应回、列表未刷、控件复活」的二次点击窗口（参照 SystemConfig 范式）。
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['feedback'] }),
      queryClient.invalidateQueries({ queryKey: ['feedback-detail', item.id] }),
    ]);

  const replyMutation = useMutation({
    // ADM-FBK-S002/S003：auto_translated=true 时服务端把 content 译为 feedback.language 后入库；
    // internal note 服务端不译不发用户（即便误勾），故 UI 在 internalNote 时隐藏翻译说明块
    // 请求取点击时的完整快照；observer选项由effect更新，不能依赖上传前的attachment闭包。
    mutationFn: (submitted: ReplyFeedbackRequest) => feedbackApi.reply(item.id, submitted),
    onSuccess: async () => {
      toast(
        internalNote ? '内部备注已记录（仅运营可见 · 不通知用户）' : `回复已发送给 ${displayName}`,
        { tone: 'pos', icon: 'IconPaperPlane' },
      );
      window.localStorage.removeItem(draftKey);
      setReply('');
      setAttachment(null);
      setTransFailed(false);
      setAmbiguousFailure(false);
      await invalidate();
    },
    onError: async (err) => {
      // F6 失败分类（reply **无幂等键** → 必须区分「确定不落库」与「可能已落库」）：
      //  ① FEEDBACK_TRANSLATION_FAILED(422)：server 先译后单事务入库，译失败抛在事务前 → **确定不落库**。
      //     草稿保留 + 失败态块的「重试」可安全直接重发（同草稿同结果，不会重复）。
      //  ② 其余（5xx / 网络 / 解码失败 / 其它非确定错误）：POST **可能已落库但响应丢失**。无幂等键直接重发
      //     会产生**重复回复** → 进 ambiguousFailure 态：禁用直接重发，要求运营看刷新后的 thread 确认再决定。
      //  ③ FEEDBACK_NOT_REPLYABLE(422)：终态（如 RESOLVED）守卫在写入前抛 → **确定不落库**，但非翻译失败
      //     （重发同样会被拒，stale 视图刷新后 composer 即隐藏）。归入「确定不落库」分支，不进 ambiguous
      //     （codex PR121 review P2：否则误显「回复可能已发送」+ 锁重试，server 已保证无写）。
      const code = err instanceof AdminApiError ? err.code : undefined;
      if (code === 'FEEDBACK_TRANSLATION_FAILED') {
        setTransFailed(true);
        setAmbiguousFailure(false);
      } else if (code === 'FEEDBACK_NOT_REPLYABLE') {
        setTransFailed(false);
        setAmbiguousFailure(false);
      } else {
        setAmbiguousFailure(true);
        setTransFailed(false);
      }
      toast(err instanceof Error ? err.message : '回复发送失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
      // 模糊失败也 refetch detail/list 对账：若回复其实已落库，刷新后 thread 会显示真实那条，运营据此判断是否
      // 真需重发（避免误重发）；草稿在两类失败下都保留（不丢运营已写内容）。
      await invalidate();
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => feedbackApi.close(item.id),
    onSuccess: async () => {
      // 服务端 closeFeedback 同事务写 feedback_status_log → 文案为真值
      toast(`工单 ${item.id} 已关闭 · 状态流转已记录`, { tone: 'pos', icon: 'IconCheck' });
      await invalidate();
    },
    onError: async (err) => {
      toast(err instanceof Error ? err.message : '关闭工单失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
      // 关闭是模糊提交（POST 可能已成功）→ refetch 对账服务端真值，不盲信旧态（F4）
      await invalidate();
    },
  });

  /**
   * dd#960 选图：本地预拦截（类型 / 大小）→ pre-create → PUT R2。
   * 白名单与上限**直接取服务端同一份常量**（`@novel-admin/shared`），不另写一套 ——
   * 前端放宽于服务端会让运营「选得进、发不出」。
   */
  async function onPickImage(file: File) {
    // 回复请求在飞时拒绝选图（codex P2）：那条 POST 的 body 已经发出去了，此刻选的图不可能被带上；
    // 而它的 onSuccess 会 setAttachment(null) —— 图既没发出去、又从 composer 里静默消失。
    // 按钮已加 replyMutation.isPending 到 disabled，这里是 handler 侧的第二道（防隐藏 input 被别的路径触发）。
    if (replyMutation.isPending) return;
    if (!FEEDBACK_ATTACHMENT_CONTENT_TYPES.includes(file.type as never)) {
      toast(`仅支持 ${FEEDBACK_ATTACHMENT_CONTENT_TYPES.join(' / ')}（服务端同一份白名单）`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      return;
    }
    if (file.size <= 0 || file.size > FEEDBACK_ATTACHMENT_MAX_SIZE_BYTES) {
      toast(`图片需 > 0 且不超过 ${FEEDBACK_ATTACHMENT_MAX_SIZE_BYTES / 1024 / 1024} MB`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      return;
    }
    setAttachUploading(true);
    try {
      const pre = await feedbackApi.createAttachment(item.id, {
        content_type: file.type,
        size_bytes: file.size,
      });
      await putAttachmentToSignedUrl(pre.signed_upload_url, file, file.type);
      setAttachment({
        id: pre.attachment_id,
        // 本地预览：服务端副本在绑定前读不到（A7 §3.5）
        previewUrl: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
      });
      // **不清 ambiguousFailure**（codex P2）：它的语义是「上一条 reply 可能已落库、本接口无幂等键，
      // 需运营先看刷新后的 thread 确认再放行重发」。传一张图跟「上次那条发出去没有」毫无关系，
      // 在这里清掉等于绕过 F6 防重复回复的闸 —— 运营会带着原草稿直接重发，可能落两条回复。
      // 解除该态的唯一入口仍是警示块里那个「已确认未发出 · 放行重发」按钮。
    } catch (err) {
      // 失败不留半吊子状态：不设 attachment，运营重选即重新 pre-create（旧 orphan 由 24h cron 清）
      toast(err instanceof Error ? err.message : '图片上传失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    } finally {
      setAttachUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // 发送禁用：内容超限 / 发送中 / 空内容 / 无 reply 权限（viewer，F1）/ 模糊失败未确认（F6）。
  // canReply=false 时永远 disabled → viewer 无法发出注定 403 的 reply 请求。
  // ambiguousFailure 时禁用直接重发：上次 POST 可能已落库（无幂等键），需运营先看刷新后的 thread 确认
  // 是否已发出，再显式 acknowledge 才放行（防重复回复，F6）。
  // dd#960 组合约束（A7 §3）：content 与附件**至少一者**非空（与 C 端「恰好一者」相反，图文可并存）
  const replyEmpty = reply.trim().length === 0 && !attachment;
  // 新发、翻译失败重试都从当前composer显式取值，图文不会因mutation调度延迟丢失。
  const replySubmission: ReplyFeedbackRequest = {
    content: reply.trim().length > 0 ? reply : null,
    attachment_ids: attachment ? [attachment.id] : [],
    is_internal_note: internalNote,
    auto_translated: autoTrans,
  };
  const sendDisabled =
    !canReply ||
    over ||
    replyMutation.isPending ||
    replyEmpty ||
    attachUploading ||
    ambiguousFailure;
  const sendLabel = replyMutation.isPending
    ? autoTrans && !internalNote
      ? '翻译中…'
      : '发送中…'
    : internalNote
      ? '记录备注'
      : '发送回复';
  const sendLabelHint = attachUploading
    ? '图片上传中…'
    : replyEmpty
      ? '回复内容不能为空（文字与图片至少填一项）'
      : '';

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
        <div className="hstack" style={{ gap: 10 }}>
          <Avatar seed={item.id} size={36} style={{ fontSize: 'inherit' }}>
            {displayName[0]}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="hstack" style={{ gap: 8 }}>
              <span style={{ fontWeight: 500, fontSize: 14 }}>{displayName}</span>
              {hasAccount && (
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  · {item.user.id}
                </span>
              )}
              <span className="chip" style={{ fontSize: 10 }}>
                {appName}
              </span>
              <span
                className="chip chip-info"
                title="反馈实际语言 · 回复翻译目标"
                style={{ fontSize: 10 }}
              >
                <Icons.IconLanguages size={9} />
                反馈语言 {fmtLang(item.lang)}
              </span>
            </div>
            {/* 设计稿此行另有国家 / 累计充值：用户详情 DTO 无 country、total_spend 法币源迁移期
                unavailable（#28b）→ 数据无源不编，仅展示 contact_email + 反馈语言（见计划 §五） */}
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
              <span className="mono" title="contact_email">
                {contactEmail || '—'}
              </span>{' '}
              · {fmtLang(item.lang)}
            </div>
          </div>
          <div className="hstack" style={{ gap: 6 }}>
            <button
              className="btn btn-sm"
              title="查看用户详情"
              disabled={!hasAccount}
              onClick={() => setUserDetailOpen(true)}
            >
              <Icons.IconUsers size={11} />
              用户详情
            </button>
            {/* 流转历史：detail.status_log（feedback_status_log 镜像，created_at ASC）接真时间线弹窗 */}
            <button
              className="btn btn-sm"
              disabled={statusLog.length === 0}
              title={statusLog.length === 0 ? '暂无状态流转记录' : undefined}
              style={statusLog.length === 0 ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
              onClick={() => setStatusLogOpen(true)}
            >
              <Icons.IconHistory size={11} />
              流转历史
            </button>
            {/* 发放 BONUS：root 专属（后端 grant-bonus requireRole('root')）→ 非 root 禁用入口（F2） */}
            <button
              className="btn btn-sm"
              disabled={!canGrantAccountBonus}
              title={
                !hasAccount
                  ? '设备反馈无账号归属'
                  : canGrantBonus
                    ? undefined
                    : FORBIDDEN_ROLE_TITLE
              }
              style={canGrantBonus ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
              onClick={() => setBonusOpen(true)}
            >
              <Icons.IconCoin size={11} />
              BONUS
            </button>
            {/* 关闭工单：root/admin（后端 close requireRole）→ operator/viewer 禁用入口（F1） */}
            <button
              className="btn btn-sm"
              disabled={!canClose || closed || closeMutation.isPending}
              title={!canClose ? FORBIDDEN_ROLE_TITLE : closed ? '工单已关闭' : undefined}
              style={canClose ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
              onClick={() => setCloseConfirm(true)}
            >
              <Icons.IconCheck size={11} />
              关闭工单
            </button>
            <button
              className="btn btn-ghost btn-icon"
              disabled
              title={`更多操作（转交 / 标记 / 拉黑）：dev-docs ADM-FBK 仅 S001~S004，无对应需求与端点 · ${PLAN_REF}`}
            >
              <Icons.IconMoreV size={13} />
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {detail?.device_id && (
          <div className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>提交设备</div>
            <div className="mono" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>
              {detail.device_id}
            </div>
            <div style={{ fontSize: 12, marginTop: 8 }}>
              同 App 历史关联账号：
              {detail.related_device_users?.length
                ? detail.related_device_users
                    .map((user) => user.user_name ?? user.user_id)
                    .join('、')
                : '无记录'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 6 }}>
              仅供排查；关联账号不代表工单归属，回复仍使用本工单的通知渠道。
            </div>
          </div>
        )}
        {/* user msg（真详情：description 正文 + translation 中文翻译，ADM-FBK-S001） */}
        <div className="card" style={{ padding: 14 }}>
          <div className="hstack" style={{ marginBottom: 8, gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>{displayName}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
              {fmtDateTime(item.createdAt)}
            </span>
            <div style={{ flex: 1 }} />
            {translation?.status === 'OK' && (
              <span className="chip" style={{ fontSize: 10 }}>
                <Icons.IconLanguages size={9} />
                已自动翻译
              </span>
            )}
            {translation?.status === 'FAILED' && (
              <span className="chip chip-warn" style={{ fontSize: 10 }}>
                <Icons.IconLanguages size={9} />
                翻译失败
              </span>
            )}
          </div>
          {isLoading && <div style={{ fontSize: 12, color: 'var(--fg-faint)' }}>加载详情…</div>}
          {detail && (
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.6,
                color: 'var(--fg)',
                display: showOriginal || translation?.status !== 'OK' ? 'block' : 'none',
              }}
            >
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{detail.subject}</div>
              {detail.description}
            </div>
          )}
          {translation?.status === 'OK' && (
            <div
              style={{
                fontSize: 12.5,
                lineHeight: 1.55,
                color: 'var(--fg-muted)',
                marginTop: 10,
                padding: 10,
                borderLeft: '2px solid var(--accent)',
                background: 'var(--accent-soft)',
                borderRadius: 2,
              }}
            >
              <div className="hstack" style={{ marginBottom: 4 }}>
                <span className="label">用户反馈翻译（中文）</span>
                <div style={{ flex: 1 }} />
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ height: 20, padding: '0 6px', fontSize: 10.5 }}
                  onClick={() => setShowOriginal((v) => !v)}
                >
                  {showOriginal ? '隐藏原文' : `查看原文 (${fmtLang(item.lang)})`}
                </button>
              </div>
              {translation.subject && (
                <div style={{ fontWeight: 500, marginBottom: 4 }}>{translation.subject}</div>
              )}
              {translation.description}
            </div>
          )}
          {translation?.status === 'FAILED' && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--warn)' }}>
              中文翻译失败（DeepL 暂不可用）· 已显示原文，可刷新重试
            </div>
          )}
          {/* 用户附件预览（ADM-FBK-S001）接真：detail.attachments（仅 CLEAN，PII 边界）。
              缩略经鉴权代理流读取（FeedbackAttachmentThumb authedFetch→blob），不暴露 object_key。 */}
          {detail && detail.attachments.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="label" style={{ marginBottom: 6 }}>
                用户附件（{detail.attachments.length}）
              </div>
              <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
                {detail.attachments.map((a) => (
                  <FeedbackAttachmentThumb key={a.id} att={a} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 已关闭说明卡 · ADM-FBK-S004（来源区分需 status_log，DTO 未暴露 → 统一文案不编 actor） */}
        {closed && (
          <div
            className="card-flat"
            style={{ padding: '10px 14px', background: 'var(--bg-subtle)' }}
          >
            <div className="hstack" style={{ gap: 8 }}>
              <Icons.IconInfo size={13} style={{ color: 'var(--info)' }} />
              <span style={{ fontSize: 12, color: 'var(--fg-muted)', flex: 1 }}>
                此工单已关闭，无法继续回复 · 运营手动关闭，或运营回复后用户 14
                天内未再回复由系统自动关闭（ADM-FBK-S004）
              </span>
            </div>
          </div>
        )}

        {/* 已发回复 thread（真数据 detail.replies；含内部备注，按时间升序） */}
        {detail &&
          [...detail.replies]
            .sort((a, b) => a.created_at.localeCompare(b.created_at))
            .map((r) => (
              <div
                key={r.id}
                className="card"
                style={{ padding: 14, background: 'var(--bg-subtle)' }}
              >
                <div className="hstack" style={{ marginBottom: 8, gap: 6 }}>
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: 'var(--accent)',
                      color: 'white',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {(r.is_from_support ?? r.replied_by != null) ? 'A' : 'U'}
                  </span>
                  {/* server #188：线程含运营 + 用户双方回复，按来源区分；用户回复 replied_by 为 null。
                      版本错配兼容（codex PR121 adversarial）：旧 server 响应缺 is_from_support → 回退按 replied_by 推断
                      （有=运营 / null=用户），既不把存量运营回复误标用户、也不把用户跟进误标运营。 */}
                  <span style={{ fontSize: 12, fontWeight: 500 }}>
                    {(r.is_from_support ?? r.replied_by != null) ? '运营' : '用户'}
                  </span>
                  {r.replied_by && (
                    <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                      {r.replied_by}
                    </span>
                  )}
                  {r.is_internal_note && (
                    <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
                      内部备注 · 仅运营可见
                    </span>
                  )}
                  <div style={{ flex: 1 }} />
                  <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                    {fmtDateTime(r.created_at)}
                  </span>
                </div>
                {/* dd#960：content 可为 null（纯图片回复，A7 §3）→ 不渲染空文本行，只出图。
                    但**附件也为空**时（数据异常 / 全部非 CLEAN 被过滤）仍给一句占位，
                    否则是个什么都没有的空气泡、看着像渲染坏了。 */}
                {r.content ? (
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>{r.content}</div>
                ) : (
                  r.attachments.length === 0 && (
                    <div style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--fg-faint)' }}>
                      （仅图片，无正文）
                    </div>
                  )
                )}
                {r.translation?.status === 'OK' && (
                  <div
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      color: 'var(--fg-muted)',
                      marginTop: 10,
                      padding: 10,
                      borderLeft: '2px solid var(--accent)',
                      background: 'var(--accent-soft)',
                      borderRadius: 2,
                    }}
                  >
                    <div className="label" style={{ marginBottom: 4 }}>
                      运营回复翻译（中文）
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{r.translation.content}</div>
                  </div>
                )}
                {r.translation?.status === 'FAILED' && (
                  <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--warn)' }}>
                    回复中文翻译失败 · 已保留原文，可稍后刷新重试
                  </div>
                )}
                {/* 该条回复的附件（按 reply_id 分组，A7 §2.1）——与工单头部「提交时附件」归属层级不同、
                    项形态相同，同样经 §2.2 鉴权代理读（authedFetch→blob，非裸 <img src>） */}
                {r.attachments.length > 0 && (
                  <div
                    className="hstack"
                    style={{ gap: 8, flexWrap: 'wrap', marginTop: r.content ? 8 : 0 }}
                  >
                    {r.attachments.map((a) => (
                      <FeedbackAttachmentThumb key={a.id} att={a} />
                    ))}
                  </div>
                )}
              </div>
            ))}

        {/* Composer：待回复/处理中/已回复 均可回复（server #190 起用户可对已回复单跟进，运营须能继续应答；
            server replyFeedback 接受任意非关闭单）。已关闭/已解决不显（终态）。 */}
        {(item.status === '待回复' || item.status === '处理中' || item.status === '已回复') && (
          <div className="card" style={{ padding: 14 }}>
            <div className="hstack" style={{ marginBottom: 8, gap: 6 }}>
              <span style={{ fontWeight: 500, fontSize: 13 }}>回复</span>
              <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>· 默认以英文输入</span>
              <div style={{ flex: 1 }} />
              <label
                className="hstack"
                style={{ gap: 6, fontSize: 12, cursor: 'pointer' }}
                title="提交时由服务端 DeepL 翻译 · 失败返回 FEEDBACK_TRANSLATION_FAILED(422) 不落库"
              >
                <input
                  type="checkbox"
                  checked={autoTrans}
                  onChange={(e) => {
                    setAutoTrans(e.target.checked);
                    setTransFailed(false);
                  }}
                />
                <span>
                  自动翻译为 <span className="strong">{fmtLang(item.lang)}</span> (反馈实际语言)
                  后提交
                </span>
              </label>
            </div>
            <textarea
              className="input"
              rows={5}
              value={reply}
              onChange={(e) => {
                setReply(e.target.value);
                setTransFailed(false);
              }}
              style={{
                height: 'auto',
                lineHeight: 1.55,
                fontSize: 13,
                borderColor: over ? 'var(--neg)' : undefined,
              }}
            />
            <div className="hstack" style={{ marginTop: 4 }}>
              <label
                className="hstack"
                style={{ gap: 6, fontSize: 11.5, cursor: 'pointer', color: 'var(--fg-muted)' }}
                title="feedback_replies.is_internal_note · 仅运营可见，用户 C 端返回自动过滤"
              >
                <input
                  type="checkbox"
                  checked={internalNote}
                  onChange={(e) => setInternalNote(e.target.checked)}
                />
                <span>内部备注（仅运营可见 · 不发送给用户）</span>
              </label>
              <div style={{ flex: 1 }} />
              <span
                className="mono"
                style={{ fontSize: 11, color: over ? 'var(--neg)' : 'var(--fg-faint)' }}
              >
                {reply.length.toLocaleString()} / {MAX}
              </span>
            </div>
            {over && (
              <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--neg)' }}>
                <Icons.IconAlert size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                内容超过 5000 字符上限，请缩短后发送
              </div>
            )}
            {/* F6 模糊失败警示：上次发送可能已落库（无幂等键）→ 禁用直接重发，要求运营先看上方刷新后的回复
                thread 确认是否已发出，再 acknowledge 放行重发，避免重复回复 */}
            {ambiguousFailure && (
              <div
                style={{
                  marginTop: 8,
                  padding: 10,
                  borderRadius: 4,
                  background: 'var(--bg-subtle)',
                  borderLeft: '2px solid var(--neg)',
                  fontSize: 12,
                  color: 'var(--neg)',
                  lineHeight: 1.55,
                }}
              >
                <Icons.IconAlert size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                发送失败但响应丢失，回复**可能已发出**（本接口无幂等保护）。已自动刷新上方回复记录，请先确认
                是否已发送，避免重复回复。确认尚未发出后再点下方按钮放行重发。
                <button
                  className="btn btn-sm"
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    invalidate();
                    setAmbiguousFailure(false);
                  }}
                >
                  <Icons.IconRefresh size={10} />
                  已确认未发出 · 放行重发
                </button>
              </div>
            )}
            {autoTrans && !internalNote && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  borderRadius: 4,
                  background: 'var(--bg-subtle)',
                  borderLeft: transFailed ? '2px solid var(--neg)' : '2px solid var(--accent)',
                }}
              >
                <div className="hstack" style={{ marginBottom: 4, gap: 6 }}>
                  <Icons.IconSparkles size={11} style={{ color: 'var(--accent)' }} />
                  <span className="label">提交时翻译为 {fmtLang(item.lang)} · 反馈实际语言</span>
                </div>
                {/* 翻译在服务端提交时执行（无预览端点）→ 不渲染假译文预览；
                    loading = 发送请求期间（服务端正在调 DeepL），failed = 上次发送返回
                    FEEDBACK_TRANSLATION_FAILED 的真实失败态（重试 / 取消翻译用原文发送） */}
                {replyMutation.isPending && (
                  <div className="hstack" style={{ gap: 8, padding: '6px 0' }}>
                    <Icons.IconRefresh size={11} style={{ color: 'var(--fg-muted)' }} />
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>DeepL 翻译中…</span>
                  </div>
                )}
                {!replyMutation.isPending && transFailed && (
                  <div style={{ fontSize: 12, color: 'var(--neg)', lineHeight: 1.5 }}>
                    翻译失败：FEEDBACK_TRANSLATION_FAILED (422) · 回复未发送，草稿已保留
                    <button
                      className="btn btn-sm"
                      style={{ marginLeft: 8 }}
                      disabled={sendDisabled}
                      onClick={() => replyMutation.mutate(replySubmission)}
                    >
                      <Icons.IconRefresh size={10} />
                      重试
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{ marginLeft: 4 }}
                      onClick={() => {
                        setAutoTrans(false);
                        setTransFailed(false);
                      }}
                    >
                      取消翻译 · 用英文原文发送
                    </button>
                  </div>
                )}
                {!replyMutation.isPending && !transFailed && (
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.6 }}>
                    回复将由服务端 DeepL 翻译为 {fmtLang(item.lang)} 后入库并通知用户；
                    翻译失败将不会发送（返回 FEEDBACK_TRANSLATION_FAILED，原文不落库）。
                  </div>
                )}
              </div>
            )}
            {/* dd#960 已选图片：**本地** objectURL 预览（绑定前服务端副本读不到，A7 §3.5）。
                内部备注同样开放带图（A7 §3「同样支持带图」）。 */}
            {attachment && (
              <div
                className="hstack"
                style={{
                  gap: 10,
                  marginTop: 10,
                  padding: 8,
                  borderRadius: 4,
                  background: 'var(--bg-subtle)',
                }}
              >
                <img
                  src={attachment.previewUrl}
                  alt="待发送图片"
                  style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 4 }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {attachment.name}
                  </div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {Math.max(1, Math.round(attachment.size / 1024))} KB · 已上传，随本条回复发送
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => setAttachment(null)}
                  title="移除后本次不发送该图片（已上传副本 24h 内未被引用会由 orphan cron 清理）"
                >
                  移除
                </button>
              </div>
            )}
            <div className="hstack" style={{ gap: 8, marginTop: 10 }}>
              {/* dd#960 运营回复带图（A7 §3 / §3.5）：单条上限 1 张 → 已选时禁用再选。
                  accept 与本地预拦截都取服务端同一份白名单常量，避免前端放宽于服务端。 */}
              <input
                ref={fileInputRef}
                type="file"
                accept={FEEDBACK_ATTACHMENT_CONTENT_TYPES.join(',')}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void onPickImage(file);
                }}
              />
              <button
                className="btn"
                disabled={
                  !canReply || attachUploading || attachment !== null || replyMutation.isPending
                }
                title={
                  !canReply
                    ? FORBIDDEN_ROLE_TITLE
                    : replyMutation.isPending
                      ? '回复发送中，请等本条发完再选图（此刻选的图不会被带上）'
                      : attachment !== null
                        ? '单条回复最多 1 张图片（要发多张就发多条回复）'
                        : attachUploading
                          ? '上传中…'
                          : `${FEEDBACK_ATTACHMENT_CONTENT_TYPES.join(' / ')} · 最大 ${FEEDBACK_ATTACHMENT_MAX_SIZE_BYTES / 1024 / 1024} MB`
                }
                onClick={() => fileInputRef.current?.click()}
              >
                <Icons.IconImg size={12} />
                {attachUploading ? '上传中…' : '插入图片'}
              </button>
              {/* 附 BONUS 发放：root 专属（后端 grant-bonus requireRole('root')）→ 非 root 禁用（F2） */}
              <button
                className="btn"
                disabled={!canGrantAccountBonus}
                title={
                  !hasAccount
                    ? '设备反馈无账号归属'
                    : canGrantBonus
                      ? undefined
                      : FORBIDDEN_ROLE_TITLE
                }
                style={canGrantBonus ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                onClick={() => setBonusOpen(true)}
              >
                <Icons.IconCoin size={12} />附 BONUS 发放
              </button>
              <div style={{ flex: 1 }} />
              <span
                style={{ fontSize: 11, color: 'var(--fg-muted)' }}
                title="ADM-FBK-S004 · 运营回复后 14 天用户未再回复，cron 自动关闭（actor_type=SYSTEM）"
              >
                14 天未回复将自动关闭{' '}
                <Icons.IconInfo size={10} style={{ verticalAlign: 'middle' }} />
              </span>
              <button
                className="btn"
                onClick={() => {
                  window.localStorage.setItem(draftKey, reply);
                  toast('草稿已保存到本地 · 下次打开自动恢复', { tone: 'pos', icon: 'IconCheck' });
                }}
              >
                保存草稿
              </button>
              <button
                className="btn btn-accent"
                disabled={sendDisabled}
                title={
                  !canReply
                    ? FORBIDDEN_ROLE_TITLE
                    : over
                      ? '超过 5000 字符上限'
                      : replyMutation.isPending
                        ? '发送中…'
                        : ambiguousFailure
                          ? '上次发送响应丢失、可能已发出（无幂等键）· 请先确认刷新后的回复记录再放行重发'
                          : sendLabelHint
                }
                onClick={() => replyMutation.mutate(replySubmission)}
              >
                <Icons.IconPaperPlane size={11} />
                {sendLabel}
              </button>
            </div>
          </div>
        )}
      </div>

      {userDetailOpen && hasFeedbackAccount(item.user.id) && (
        <FeedbackUserDetailDrawer userId={item.user.id} onClose={() => setUserDetailOpen(false)} />
      )}
      {closeConfirm && (
        <ConfirmDialog
          title={`关闭工单 ${item.id}？`}
          message="关闭后该工单不再出现在「未处理」列表，状态切换将写入 feedback_status_log。"
          confirmLabel="关闭工单"
          tone="warn"
          onConfirm={() => closeMutation.mutate()}
          onClose={() => setCloseConfirm(false)}
        />
      )}
      {bonusOpen && item.user.id !== null && (
        <GrantBonusDialog
          // F5：BONUS 弹窗从 user.appId 解析 allowed_languages、appId 空则阻断提交。feedback 列表行
          // item.user 是 adapter 占位（appId=''），直接传会让 root 也无法发 BONUS。改从 N+1 enrichedUser
          // （AdminUserDetailResponse 有 app_id/app_name）构造完整 UserRow：appId=enrichedUser.app_id（为
          // null 时回退 '' → 弹窗按设计阻断，因无 App 无法定 locale），name/email/bonus 取富集真值。
          // enrichedUser=null（N+1 失败）→ 回退 item.user（appId='' → 阻断，安全）。
          user={{
            ...item.user,
            id: item.user.id,
            name: displayName,
            email: enrichedUser?.email ?? item.user.email,
            appId: enrichedUser?.app_id ?? item.user.appId,
            appName: enrichedUser?.app_name ?? appName,
          }}
          onClose={() => setBonusOpen(false)}
        />
      )}
      {statusLogOpen && (
        <Dialog
          title="状态流转历史"
          subtitle={`工单 ${item.id} · 共 ${statusLog.length} 次流转`}
          icon="IconHistory"
          width={520}
          onClose={() => setStatusLogOpen(false)}
        >
          <div className="vstack" style={{ gap: 0, padding: 14 }}>
            {statusLog.map((s, i) => (
              <div
                key={s.id}
                className="hstack"
                style={{
                  gap: 10,
                  padding: '10px 0',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  className={`chip ${s.actor_type === 'SYSTEM' ? 'chip-info' : s.actor_type === 'USER' ? 'chip-warn' : 'chip-accent'}`}
                  style={{ fontSize: 10, flexShrink: 0 }}
                >
                  {s.actor_type === 'SYSTEM' ? '系统' : s.actor_type === 'USER' ? '用户' : '运营'}
                </span>
                <div className="vstack" style={{ gap: 3, flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 12.5 }}>
                    <span className="mono">{s.old_status}</span> →{' '}
                    <span className="mono" style={{ color: 'var(--fg-strong)', fontWeight: 500 }}>
                      {s.new_status}
                    </span>
                  </span>
                  {s.reason && (
                    <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{s.reason}</span>
                  )}
                  <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {fmtDateTime(s.created_at)}
                    {s.actor_id ? ` · ${s.actor_id.slice(0, 8)}` : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Dialog>
      )}
    </div>
  );
}
